/**
 * Pure, DOM-free logic extracted from `components/ScanReview.tsx` so it's
 * unit-testable under `bun test` — mini-apps has no jsdom/happy-dom
 * registered (documented precedent:
 * `mini-apps/action-view/__tests__/notes-save-mutation.test.tsx`'s file
 * header), and `ScanReview.tsx` itself cannot be imported at all under that
 * runtime: its `@clay-run/glaze` imports touch DOM globals (`KeyboardEvent`)
 * at MODULE-EVAL time, not just at render time, so even importing a named
 * export from that file throws `ReferenceError: KeyboardEvent is not
 * defined` before a single test runs (verified). This file has zero
 * `@clay-run/glaze`/DOM-touching imports, mirroring `enrich-clay.ts`'s and
 * `linkedin-qr.ts`'s "frontend-safe leaf" pattern — `ScanReview.tsx` imports
 * from here rather than defining these functions inline.
 */

import type { Scan, ScanSubmitResult } from './scan';

export type AiField = 'name' | 'company' | 'email' | 'linkedin';

/**
 * The `needs_review` pre-fill's fill-if-blank decision (Fix 5). Factored out
 * so it's usable both as `ScanReview.tsx`'s mount-time `useState` initializer
 * seed value and inside a live-update effect for a scan that flips
 * `submitting` → `needs_review` while the SAME form stays mounted —
 * `ActiveScanPanel.tsx` keys that component by `scan.id`, so that transition
 * re-renders it rather than remounting it, and a `useState` initializer
 * alone only ever runs once (at mount). Only ever returns a non-null value
 * for a field that is BLANK on `scan` ITSELF; the caller additionally
 * re-checks its own current (possibly rep-edited) form state before writing,
 * so neither call site can clobber a value the rep already typed.
 */
export function needsReviewPrefill(
  scan: Pick<Scan, 'status' | 'email' | 'enrichedEmail' | 'linkedin' | 'enrichedLinkedin'>,
): { email: string | null; linkedin: string | null } {
  return {
    email: scan.status === 'needs_review' && !scan.email && scan.enrichedEmail ? scan.enrichedEmail : null,
    linkedin: scan.status === 'needs_review' && !scan.linkedin && scan.enrichedLinkedin ? scan.enrichedLinkedin : null,
  };
}

/** Fields an enrichment result (auto-fire or manual "Enrich with Clay") can
 *  populate. `linkedin` is deliberately never among them — it's the
 *  routine's INPUT, never overwritten by its response (see the comment above
 *  `applyEnrichment` in `ScanReview.tsx`). */
export interface EnrichableFields {
  name: string;
  company: string;
  email: string;
}

/**
 * Computes which fields an enrichment result should actually write, given
 * the CURRENT form values and which trigger fired it (Fix 2, round-N live-QA:
 * auto-enrichment was clobbering in-progress rep edits):
 *
 * - `mode: 'manual'` (the "Enrich with Clay" button) always overwrites a
 *   non-empty routine value — the rep explicitly asked for a fresh result.
 *   This is `ScanReview.tsx`'s original, unchanged `applyEnrichment` stance.
 * - `mode: 'auto'` (the auto-fire query — up to 60s in flight while the form
 *   stays editable, see the "Enrich with Clay" button's own comment in
 *   `ScanReview.tsx`) only ever writes into a field that is STILL blank at
 *   apply time — re-checking `current`, not the mount-time snapshot that
 *   gated firing the request in the first place, so a rep who typed a real
 *   value while the request was in flight is never clobbered.
 *
 * Pure — no state, no I/O — so `ScanReview.tsx`'s `applyEnrichment` is a thin
 * wrapper that calls this and then routes the result into `setName`/
 * `setCompany`/`setEmail`/`addAiFields`.
 */
export function computeEnrichmentUpdates(
  result: { name: string | null; company: string | null; email: string | null },
  current: EnrichableFields,
  mode: 'auto' | 'manual',
): { updates: Partial<EnrichableFields>; filled: AiField[] } {
  const updates: Partial<EnrichableFields> = {};
  const filled: AiField[] = [];

  function consider(field: keyof EnrichableFields, value: string | null) {
    if (!value || value.trim() === '') return;
    if (mode === 'auto' && current[field].trim() !== '') return;
    updates[field] = value;
    filled.push(field);
  }

  consider('name', result.name);
  consider('company', result.company);
  consider('email', result.email);

  return { updates, filled };
}

/**
 * The "Enriched info" section (product-owner simplification of the old
 * `SubmitResponseModal` popup, ScanReview.tsx) — inline on the page instead
 * of a modal, and it has to render correctly whether the result came from a
 * submission fired THIS mount (a live poll) or from re-opening a scan that
 * already carries a result from a PAST session with nothing currently in
 * flight. Only the latter case has no `ScanSubmitResult` to read — the
 * routing/booking-link fields (`routingDecision`/`gtmeName`/`bookingUrl`/
 * `followupExplainer`) were never persisted onto the `Scan` row, only the
 * CRM/contact/campaign-member ids and the enriched email/linkedin were — so
 * this adapter can only reconstruct a partial equivalent of the real
 * `ScanSubmitResult`, with every routing field `null`.
 *
 * `status` isn't persisted directly either (`Scan.status` collapses both
 * `partial` and `failed` terminal outcomes into `needs_review`), so it's
 * inferred from `sfdcError`'s presence — the same heuristic
 * `ScanReview.tsx`'s own `needs_review` Alert already uses ("This lead
 * didn't fully sync" vs. the persisted error text).
 */
export function buildPersistedSubmitResult(
  scan: Pick<
    Scan,
    | 'status'
    | 'sfdcResultUrl'
    | 'sfdcCampaignMemberId'
    | 'sfdcError'
    | 'sfdcContactId'
    | 'enrichedEmail'
    | 'enrichedLinkedin'
    | 'gtmeName'
    | 'bookingLink'
  >,
  // Narrowed to the terminal (`pending: false`) arm specifically — this
  // function only ever builds a FINISHED result from already-persisted
  // fields, never a pending one, so callers (including this file's own
  // tests) can read `.status`/`.error` etc. straight off the return value
  // without re-narrowing a `pending: true` arm back out of the full
  // `ScanSubmitResult` union.
): Extract<ScanSubmitResult, { pending: false }> {
  return {
    pending: false,
    status: scan.status === 'synced' ? 'Complete' : scan.sfdcError ? 'failed' : 'partial',
    contactId: scan.sfdcContactId,
    campaignMemberId: scan.sfdcCampaignMemberId,
    crmUrl: scan.sfdcResultUrl,
    routing: { decision: null, gtmeName: scan.gtmeName, gtmeEmail: null, bookingLink: scan.bookingLink, followupExplainer: null },
    enriched: { fullName: null, company: null, email: scan.enrichedEmail, linkedin: scan.enrichedLinkedin },
    error: scan.sfdcError,
  };
}

/** What the "Enriched info" section should render, and whether the top-of-
 *  page "View results" jump button should show alongside it. */
export interface EnrichedInfoState {
  /** `null` means the section renders nothing at all — no submission has
   *  ever been fired from this mount and the scan carries no persisted
   *  result either. */
  result: ScanSubmitResult | null;
  /**
   * True only when the result came from the scan's OWN persisted fields
   * (a scan reopened later with nothing currently in flight) rather than
   * from a submission fired this mount — this is exactly the condition the
   * "View results" jump button uses, since a live in-page result needs no
   * jump button (the rep just fired it and is already looking at the page).
   */
  isPersisted: boolean;
}

/**
 * The single decision `ScanReview.tsx` needs to pick what the "Enriched
 * info" section shows: the live result (if a submission was fired this
 * mount — pending or terminal, unchanged from the old modal's `firedSeed`/
 * poll contract), a synthetic pending arm (a scan reopened while an EARLIER
 * mount's submission is still `submitting` server-side — the poll's
 * `enabled` gate is unchanged and does not re-arm for this case, so there is
 * no live result to read, but the row is not blank either), the persisted
 * adapter (a scan reopened with an already-terminal `synced`/`needs_review`
 * row and nothing in flight), or nothing (a fresh draft/error scan with no
 * submission history at all).
 *
 * `displayedResult` is `ScanReview.tsx`'s existing
 * `submitStatusQuery.data?.result ?? firedSeed` — this function's first
 * branch is "trust that unchanged" so the live poll path never regresses,
 * with one carve-out (QA-fix batch, Fix 2): a PENDING `displayedResult` is
 * only trusted while `scan.status` itself still agrees the run is in
 * flight. The per-scan submit-status poll (`useSubmitStatusQuery`) and the
 * scans-LIST poll (`scan.ts`'s `scansListPollInterval`, which finalizes
 * `submitting` rows server-side on every `GET /scans` read) are two
 * independent observers of the same run — if the per-scan poll silently
 * stops advancing (a dropped request, a backgrounded tab) while the list
 * poll still lands and flips `scan.status` to a terminal `synced`/
 * `needs_review`, a stale `{pending: true}` `displayedResult` would
 * otherwise leave the "Enriched info" panel stuck on "submitting" forever.
 * A genuinely non-pending `displayedResult` (a live terminal outcome) is
 * unaffected — only the pending arm is re-checked against `scan.status`.
 */
export function resolveEnrichedInfoState(
  scan: Pick<
    Scan,
    | 'status'
    | 'sfdcResultUrl'
    | 'sfdcCampaignMemberId'
    | 'sfdcError'
    | 'sfdcContactId'
    | 'enrichedEmail'
    | 'enrichedLinkedin'
    | 'gtmeName'
    | 'bookingLink'
  >,
  displayedResult: ScanSubmitResult | null,
): EnrichedInfoState {
  const staleOrLivePending = displayedResult !== null && displayedResult.pending === true;
  const scanIsTerminal = scan.status === 'synced' || scan.status === 'needs_review';
  if (displayedResult !== null && !(staleOrLivePending && scanIsTerminal)) {
    return { result: displayedResult, isPersisted: false };
  }
  if (scan.status === 'submitting') return { result: { pending: true }, isPersisted: false };
  if (scanIsTerminal) {
    return { result: buildPersistedSubmitResult(scan), isPersisted: true };
  }
  return { result: null, isPersisted: false };
}
